import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import FormNavigation from 'components/FormNavigation';
import { FlowComponentType } from 'routes/types';
import { useDispatch, useSelector } from 'react-redux';
import { YourIncomeVariable } from 'enums/LoanFormVariables';
import FormContainer from 'components/LoanForm/FormContainer';
import CheckboxSmall from 'components/Checkbox/CheckboxSmall';
import Button from 'components/Button';
import MonthYearPicker from 'components/MonthYearPicker';
import InputSelect from 'components/InputSelect';
import { InputSelectOption } from 'components/InputSelect/InputSelect';
import { useForm } from 'react-hook-form';
import { getMessageForRequiredFields } from 'utils/errors';
import { formatMonthYear, getYearsFromNow, isLessThanMonthsOld } from 'utils/dateUtils';
import { CREDENTIAL_MINIMUM_AGE_MONTHS, getAllCredentials, getHighestTierCredential } from 'utils/credentialUtils';
import Input from 'components/Input';
import { setYourIncomeData } from 'handlers/yourIncome';
import { PreQualificationData, setPartnerName } from 'handlers/preQualificationData';
import { YourEmployerResult } from 'enums/FlowNextResults';
import { getPartnerFromEmployerName, PARTNER_DATA, PartnerType } from 'enums/PartnerName';
import { getPreQualificationData } from 'selectors/preQualificationData';

import { validateEmployerProvidedIncome } from 'thunks';
import { RootState } from 'handlers';
import { getYourIncome } from 'selectors/yourIncome';

import styles from './YourEmployer.module.scss';

export enum YourEmployerInputLabel {
  EmployerName = 'Primary employer name',
  NotCurrentlyEmployed = 'Not currently employed',
  CredentialAcquisitionDate = 'Date you first got your [user-credentials] credential',
  AdditionalCredential = 'Do you have any other credentials?',
  NoOtherCredentials = "I don't have any other credentials",
  AdditionalCredentialAcquisitionDate = 'Date you first got this credential',
}

const replaceCredentialPlaceholder = (label: string, credential: string) =>
  label.replace('[user-credentials]', credential);

export enum YourEmployerEditFocus {
  Employer = 'employer',
  CredentialDate = 'credentialDate',
}

export interface YourEmployerState {
  editFocus?: YourEmployerEditFocus;
}

const YourEmployer = ({ navigationInfo, handleNext }: FlowComponentType): JSX.Element => {
  const dispatch = useDispatch();
  const editFocus = (useLocation().state as YourEmployerState | null)?.editFocus;
  const isEmployerEdit = editFocus === YourEmployerEditFocus.Employer;
  const isCredentialDateEdit = editFocus === YourEmployerEditFocus.CredentialDate;
  const {
    yourBirthDate: { birth_date: birthDate },
    yourContact: yourContactData,
    yourName: yourNameData,
    yourIncome: yourIncomeData,
    yourAddress: yourDetailsData,
    employers: {
      validateEmployerProvidedIncome: { incomeRequired, isLoading: isIncomeVerificationLoading },
    },
  } = useSelector((state: RootState) => state);
  const [shouldRedirectAfterIncomeVerification, setShouldRedirectAfterIncomeVerification] = useState(false);
  const { partnerId: partnerName } = useSelector(getPreQualificationData);
  const defaultValues = useSelector(getYourIncome);
  const hasAnsweredAdditionalCredential =
    !isEmployerEdit &&
    Boolean(
      defaultValues[YourIncomeVariable.NoOtherCredentials] || defaultValues[YourIncomeVariable.AdditionalCredential],
    );
  const [shouldShowAdditionalCredential, setShouldShowAdditionalCredential] = useState(hasAnsweredAdditionalCredential);
  const shouldShowAdditionalCredentialRef = useRef(hasAnsweredAdditionalCredential);
  const { credentials } = yourNameData;
  const showEmployerFields = !isCredentialDateEdit;
  const shouldShowCredentialAcquisitionDate = isCredentialDateEdit || (!isEmployerEdit && !!credentials);
  const minDate = getYearsFromNow(-50);
  const maxDate = getYearsFromNow(0);
  const highestTierCredential = credentials ? getHighestTierCredential(credentials) : '';
  const {
    formState: { errors, isValid },
    register,
    unregister,
    watch,
    trigger,
    setValue,
    clearErrors,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      ...defaultValues,
      ...(!editFocus &&
        partnerName &&
        PARTNER_DATA[partnerName]?.type === PartnerType.Employer && {
          [YourIncomeVariable.EmployerName]: PARTNER_DATA[partnerName]?.fullName,
        }),
    },
  });

  const watcher = watch();
  const noOtherCredentials = Boolean(watcher[YourIncomeVariable.NoOtherCredentials]);
  const additionalCredential = watcher[YourIncomeVariable.AdditionalCredential];
  const additionalAcquisitionDate = watcher[YourIncomeVariable.AdditionalCredentialAcquisitionDate];
  const isAdditionalCredentialComplete =
    !shouldShowAdditionalCredential || noOtherCredentials || Boolean(additionalCredential && additionalAcquisitionDate);

  useEffect(() => {
    if (shouldRedirectAfterIncomeVerification && !isIncomeVerificationLoading) {
      setShouldRedirectAfterIncomeVerification(false);
      if (incomeRequired) {
        handleNext(YourEmployerResult.IncomeRequired);
      } else {
        handleNext();
      }
    }
  }, [incomeRequired, isIncomeVerificationLoading, shouldRedirectAfterIncomeVerification, handleNext]);

  const validateEmploymentFields = (errorMessage: string) => (value: any) => {
    const isNotEmployed = watch()[YourIncomeVariable.NotEmployed];

    return isNotEmployed || (value !== null && value !== undefined && value !== '') ? true : errorMessage;
  };

  const validateCredentialAcquisitionDateField = (errorMessage: string) => (value: string) => {
    if (!value) return errorMessage;

    const parsedDate = new Date(value);

    if (parsedDate < minDate) return `Date must be after ${formatMonthYear(minDate)}`;
    if (parsedDate > maxDate) return `Date must be before or equal to ${formatMonthYear(maxDate)}`;
  };

  const credentialDateLabel = highestTierCredential
    ? replaceCredentialPlaceholder(YourEmployerInputLabel.CredentialAcquisitionDate, highestTierCredential)
    : 'Credential date';

  useEffect(() => {
    if (showEmployerFields) {
      register(YourIncomeVariable.EmployerName, {
        validate: validateEmploymentFields(getMessageForRequiredFields(YourEmployerInputLabel.EmployerName)),
      });
      register(YourIncomeVariable.NotEmployed, {
        validate: validateEmploymentFields(getMessageForRequiredFields(YourEmployerInputLabel.NotCurrentlyEmployed)),
      });
    } else {
      unregister(YourIncomeVariable.EmployerName);
      unregister(YourIncomeVariable.NotEmployed);
    }

    if (shouldShowCredentialAcquisitionDate) {
      register(YourIncomeVariable.CredentialAcquisitionDate, {
        validate: validateCredentialAcquisitionDateField(getMessageForRequiredFields(credentialDateLabel)),
      });
    } else {
      unregister(YourIncomeVariable.CredentialAcquisitionDate);
    }
  }, [register, unregister, showEmployerFields, shouldShowCredentialAcquisitionDate, credentialDateLabel]);

  useEffect(() => {
    if (!shouldShowAdditionalCredential) {
      unregister(YourIncomeVariable.AdditionalCredential);
      unregister(YourIncomeVariable.NoOtherCredentials);
      unregister(YourIncomeVariable.AdditionalCredentialAcquisitionDate);
      return;
    }

    register(YourIncomeVariable.NoOtherCredentials);

    if (noOtherCredentials) {
      clearErrors([YourIncomeVariable.AdditionalCredential, YourIncomeVariable.AdditionalCredentialAcquisitionDate]);
      unregister(YourIncomeVariable.AdditionalCredential);
      unregister(YourIncomeVariable.AdditionalCredentialAcquisitionDate);
      return;
    }

    register(YourIncomeVariable.AdditionalCredential, {
      required: getMessageForRequiredFields(YourEmployerInputLabel.AdditionalCredential),
    });
    register(YourIncomeVariable.AdditionalCredentialAcquisitionDate, {
      validate: additionalCredential
        ? validateCredentialAcquisitionDateField(
            getMessageForRequiredFields(
              replaceCredentialPlaceholder(YourEmployerInputLabel.CredentialAcquisitionDate, additionalCredential),
            ),
          )
        : undefined,
    });
  }, [register, unregister, clearErrors, shouldShowAdditionalCredential, noOtherCredentials, additionalCredential]);

  const hideAdditionalCredentialFields = () => {
    shouldShowAdditionalCredentialRef.current = false;
    setShouldShowAdditionalCredential(false);
    setValue(YourIncomeVariable.AdditionalCredential, '');
    setValue(YourIncomeVariable.NoOtherCredentials, false);
    setValue(YourIncomeVariable.AdditionalCredentialAcquisitionDate, undefined);
  };

  const handleContinue = async () => {
    const currentAcquisitionDate = watcher[YourIncomeVariable.CredentialAcquisitionDate];
    const isCurrentDateRecent =
      shouldShowCredentialAcquisitionDate &&
      currentAcquisitionDate &&
      isLessThanMonthsOld(currentAcquisitionDate, CREDENTIAL_MINIMUM_AGE_MONTHS);

    if (!isEmployerEdit && isCurrentDateRecent && !shouldShowAdditionalCredentialRef.current) {
      shouldShowAdditionalCredentialRef.current = true;
      setShouldShowAdditionalCredential(true);
      return;
    }

    if (!isEmployerEdit && isCurrentDateRecent && !isAdditionalCredentialComplete) return;

    if (showEmployerFields) checkForPartner(watcher[YourIncomeVariable.EmployerName] || '');

    dispatch(
      setYourIncomeData({
        ...yourIncomeData,
        ...(showEmployerFields && {
          [YourIncomeVariable.EmployerName]: watcher[YourIncomeVariable.EmployerName],
          [YourIncomeVariable.NotEmployed]: watcher[YourIncomeVariable.NotEmployed],
        }),
        ...(shouldShowCredentialAcquisitionDate && {
          [YourIncomeVariable.CredentialAcquisitionDate]: currentAcquisitionDate,
          [YourIncomeVariable.AdditionalCredential]: additionalCredential,
          [YourIncomeVariable.NoOtherCredentials]: noOtherCredentials,
          [YourIncomeVariable.AdditionalCredentialAcquisitionDate]: additionalAcquisitionDate,
        }),
      }),
    );

    if (isCredentialDateEdit || isEmployerEdit) return handleNext();

    if (showEmployerFields && watcher[YourIncomeVariable.NotEmployed]) {
      return handleNext(YourEmployerResult.IncomeRequired);
    }

    setShouldRedirectAfterIncomeVerification(true);
    dispatch(
      validateEmployerProvidedIncome({
        employerName: watcher[YourIncomeVariable.EmployerName] || '',
        borrower: {
          email: yourContactData.email,
          phoneNumber: yourContactData.phone_number,
          firstName: yourNameData.first_name,
          lastName: yourNameData.last_name,
          city: yourDetailsData.city,
          state: yourDetailsData.argyle_state,
          dateOfBirth: birthDate,
        },
      }),
    );
  };

  const checkForPartner = (employerName: string) => {
    let currentPartnerIsEmployer = false;

    if (partnerName) {
      currentPartnerIsEmployer = PARTNER_DATA[partnerName]?.type === PartnerType.Employer;
      if (!currentPartnerIsEmployer) {
        return;
      }
    }

    const partnerId = getPartnerFromEmployerName(employerName);

    if (partnerId) {
      const preQualificationData: PreQualificationData = {
        partnerId,
      };
      dispatch(setPartnerName(preQualificationData));
    } else if (currentPartnerIsEmployer) {
      // Clean up partner data if employer name was changed
      dispatch(setPartnerName({}));
    }
  };

  const handleNotEmployed = (checked: boolean) => {
    setValue(YourIncomeVariable.NotEmployed, checked);
    setValue(YourIncomeVariable.EmployerName, '');
    trigger(YourIncomeVariable.EmployerName);
  };

  const handleNoOtherCredentials = (checked: boolean) => {
    setValue(YourIncomeVariable.NoOtherCredentials, checked);
    if (checked) {
      setValue(YourIncomeVariable.AdditionalCredential, '');
      setValue(YourIncomeVariable.AdditionalCredentialAcquisitionDate, undefined);
      clearErrors([YourIncomeVariable.AdditionalCredential, YourIncomeVariable.AdditionalCredentialAcquisitionDate]);
      return;
    }

    trigger([YourIncomeVariable.AdditionalCredential, YourIncomeVariable.AdditionalCredentialAcquisitionDate]);
  };

  const handleCredentialAcquisitionDateChange = (date: string | undefined) => {
    setValue(YourIncomeVariable.CredentialAcquisitionDate, date);
    trigger(YourIncomeVariable.CredentialAcquisitionDate);

    if (shouldShowAdditionalCredential && date && !isLessThanMonthsOld(date, CREDENTIAL_MINIMUM_AGE_MONTHS)) {
      hideAdditionalCredentialFields();
    }
  };

  const selectedCredentials = (credentials ?? '')
    .split(',')
    .map((credential) => credential.trim())
    .filter(Boolean);
  const additionalCredentialOptions = getAllCredentials()
    .filter((credential) => !selectedCredentials.includes(credential))
    .map((credential) => ({ value: credential, label: credential }));

  return (
    <>
      <FormNavigation {...navigationInfo} />
      <FormContainer
        title={isCredentialDateEdit ? credentialDateLabel : 'Who is your primary employer?'}
        subtitle="We need this to pull your data."
      >
        <div className={styles.mainContainer}>
          {showEmployerFields && (
            <>
              <Input
                label={YourEmployerInputLabel.EmployerName}
                placeholder={YourEmployerInputLabel.EmployerName}
                errorMessage={errors[YourIncomeVariable.EmployerName]?.message}
                name={YourEmployerInputLabel.EmployerName}
                onChange={(event: React.FocusEvent<HTMLInputElement>) => {
                  setValue(YourIncomeVariable.EmployerName, event.target.value);
                  trigger(YourIncomeVariable.EmployerName);
                }}
                value={watcher[YourIncomeVariable.EmployerName]}
                onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
                disabled={watcher[YourIncomeVariable.NotEmployed]}
                autoFocus
              />
              <CheckboxSmall
                label={YourEmployerInputLabel.NotCurrentlyEmployed}
                name={YourIncomeVariable.NotEmployed}
                onChange={(event) => handleNotEmployed(event.target.checked)}
                checked={watcher[YourIncomeVariable.NotEmployed]}
                checkboxLabelClassName={styles.checkboxLabel}
              />
            </>
          )}
          {shouldShowCredentialAcquisitionDate && (
            <MonthYearPicker
              minDate={minDate}
              maxDate={maxDate}
              name={YourIncomeVariable.CredentialAcquisitionDate}
              label={credentialDateLabel}
              value={watcher[YourIncomeVariable.CredentialAcquisitionDate] ?? undefined}
              errorMessage={errors[YourIncomeVariable.CredentialAcquisitionDate]?.message}
              onChange={handleCredentialAcquisitionDateChange}
            />
          )}
          {shouldShowAdditionalCredential && (
            <>
              {!noOtherCredentials && (
                <InputSelect
                  label={YourEmployerInputLabel.AdditionalCredential}
                  placeholder="RN, MD, etc."
                  name={YourIncomeVariable.AdditionalCredential}
                  value={additionalCredential}
                  options={additionalCredentialOptions}
                  errorMessage={errors[YourIncomeVariable.AdditionalCredential]?.message}
                  onChange={(option: InputSelectOption) => {
                    setValue(YourIncomeVariable.AdditionalCredential, option.value);
                    trigger([
                      YourIncomeVariable.AdditionalCredential,
                      YourIncomeVariable.AdditionalCredentialAcquisitionDate,
                    ]);
                  }}
                  autocomplete
                />
              )}
              <CheckboxSmall
                label={YourEmployerInputLabel.NoOtherCredentials}
                name={YourIncomeVariable.NoOtherCredentials}
                onChange={(event) => handleNoOtherCredentials(event.target.checked)}
                checked={noOtherCredentials}
                checkboxLabelClassName={styles.checkboxLabel}
              />
              {!noOtherCredentials && (
                <MonthYearPicker
                  minDate={minDate}
                  maxDate={maxDate}
                  name={YourIncomeVariable.AdditionalCredentialAcquisitionDate}
                  label={
                    additionalCredential
                      ? replaceCredentialPlaceholder(
                          YourEmployerInputLabel.CredentialAcquisitionDate,
                          additionalCredential,
                        )
                      : YourEmployerInputLabel.AdditionalCredentialAcquisitionDate
                  }
                  value={additionalAcquisitionDate ?? undefined}
                  errorMessage={errors[YourIncomeVariable.AdditionalCredentialAcquisitionDate]?.message}
                  onChange={(date: string | undefined) => {
                    setValue(YourIncomeVariable.AdditionalCredentialAcquisitionDate, date);
                    trigger(YourIncomeVariable.AdditionalCredentialAcquisitionDate);
                  }}
                />
              )}
            </>
          )}
        </div>

        <Button
          onClick={handleContinue}
          disabled={!isValid || !isAdditionalCredentialComplete || isIncomeVerificationLoading}
          isLoading={isIncomeVerificationLoading}
        >
          Next
        </Button>
      </FormContainer>
    </>
  );
};

export default YourEmployer;
